import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';

interface Hotspot {
  id: string;
  x: number;
  y: number;
  title: string;
  color: string;
}

const VisualHotspotTool: React.FC = () => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [hotspots, setHotspots] = useState<Hotspot[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [hotspotTitle, setHotspotTitle] = useState('');
  const [selectedColor, setSelectedColor] = useState('#3B82F6');
  const imageRef = useRef<HTMLImageElement>(null);

  const colors = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Green', value: '#10B981' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Yellow', value: '#F59E0B' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' }
  ];

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!imageRef.current) return;
    
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setMousePos({ x, y });
  };

  const handleImageClick = (e: React.MouseEvent) => {
    if (!isAdding || !hotspotTitle.trim()) return;
    
    const rect = imageRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    const newHotspot: Hotspot = {
      id: `hotspot-${Date.now()}`,
      x,
      y,
      title: hotspotTitle,
      color: selectedColor
    };
    
    setHotspots(prev => [...prev, newHotspot]);
    setHotspotTitle('');
    setIsAdding(false);
  };

  const removeHotspot = (id: string) => {
    setHotspots(prev => prev.filter(h => h.id !== id));
  };

  const generateCode = () => {
    return hotspots.map(hotspot => 
      `{
        id: '${hotspot.id}',
        x: ${hotspot.x.toFixed(1)},
        y: ${hotspot.y.toFixed(1)},
        title: '${hotspot.title}',
        description: 'Description for ${hotspot.title}',
        issue: 'Problem description for ${hotspot.title}',
        solution: 'Solution description for ${hotspot.title}',
        productLink: '/products/applying',
        videoLink: '/videos/${hotspot.title.toLowerCase().replace(/\s+/g, '-')}'
      }`
    ).join(',\n      ');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          🎯 Visual Hotspot Tool
        </h2>
        <p className="text-gray-600 mb-6">
          Hover over the image to see live coordinates. Click "Add Hotspot" to place markers on features.
        </p>

        {/* Controls */}
        <div className="flex flex-wrap gap-4 mb-6">
          <button
            onClick={() => setIsAdding(!isAdding)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              isAdding 
                ? 'bg-red-500 text-white hover:bg-red-600' 
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {isAdding ? 'Cancel Adding' : 'Add Hotspot'}
          </button>
          
          {isAdding && (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Hotspot title (e.g., Swimming Pool)"
                value={hotspotTitle}
                onChange={(e) => setHotspotTitle(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {colors.map(color => (
                  <option key={color.value} value={color.value}>
                    {color.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Live Coordinates Display */}
        <div className="bg-gray-100 p-3 rounded-lg mb-4">
          <div className="text-sm text-gray-700">
            <strong>Live Coordinates:</strong> X: {mousePos.x.toFixed(1)}%, Y: {mousePos.y.toFixed(1)}%
          </div>
          {isAdding && (
            <div className="text-sm text-blue-600 mt-1">
              💡 Click on the image to place a hotspot at this position
            </div>
          )}
        </div>
      </div>

      {/* Image with Hotspots */}
      <div className="relative bg-gray-100 rounded-lg overflow-hidden">
        <img
          ref={imageRef}
            src="/assets/waterarmor1.png"
          alt="House for hotspot positioning"
          className="w-full h-auto cursor-crosshair"
          onMouseMove={handleMouseMove}
          onClick={handleImageClick}
        />

        {/* Mouse Position Indicator */}
        <div
          className="absolute w-4 h-4 bg-red-500 rounded-full pointer-events-none z-10"
          style={{
            left: `${mousePos.x}%`,
            top: `${mousePos.y}%`,
            transform: 'translate(-50%, -50%)',
            opacity: 0.7
          }}
        />

        {/* Hotspots */}
        {hotspots.map((hotspot) => (
          <motion.div
            key={hotspot.id}
            className="absolute w-8 h-8 rounded-full border-2 border-white shadow-lg cursor-pointer z-20"
            style={{
              left: `${hotspot.x}%`,
              top: `${hotspot.y}%`,
              transform: 'translate(-50%, -50%)',
              backgroundColor: hotspot.color
            }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileHover={{ scale: 1.2 }}
            onClick={() => removeHotspot(hotspot.id)}
          >
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 hover:opacity-100 transition-opacity">
              {hotspot.title}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Generated Code */}
      {hotspots.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Generated Code ({hotspots.length} hotspots)
          </h3>
          
          <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto mb-4">
            <pre className="text-sm">
              {`const hotspots = [
      ${generateCode()}
    ];`}
            </pre>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => {
                navigator.clipboard.writeText(generateCode());
                alert('Code copied to clipboard!');
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Copy Code
            </button>
            <button
              onClick={() => setHotspots([])}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <h4 className="font-semibold text-blue-900 mb-2">📋 Instructions:</h4>
        <ol className="text-blue-800 text-sm space-y-1">
          <li>1. Click "Add Hotspot" to start placing hotspots</li>
          <li>2. Enter a title for the hotspot (e.g., "Swimming Pool")</li>
          <li>3. Choose a color for the hotspot</li>
          <li>4. Click on the image where you want to place the hotspot</li>
          <li>5. Repeat for all features you want to highlight</li>
          <li>6. Copy the generated code and replace the hotspots in houseIllustrations.ts</li>
        </ol>
      </div>
    </div>
  );
};

export default VisualHotspotTool;
